import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Printer, Loader2, AlertTriangle, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

/**
 * Printable glossary page (Spanish + English I-589 box-by-box guide).
 * Designed to be downloaded as PDF via the browser's "Save as PDF" print dialog,
 * so a family member can fill the form at home calmly.
 */
export default function GlosarioI589() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/i589/glossary")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const partBSteps = useMemo(
    () =>
      data?.writing_workflow_for_part_b
        ? Object.entries(data.writing_workflow_for_part_b).filter(([k]) =>
            k.startsWith("step_")
          )
        : [],
    [data]
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6">
        <p className="text-zinc-700 mb-4">No se pudo cargar el glosario.</p>
        <Button onClick={() => navigate(-1)}>Volver</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Toolbar (oculto al imprimir) */}
      <div className="print:hidden sticky top-0 z-30 bg-white border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-5 py-3 flex items-center justify-between">
          <button
            data-testid="glossary-back"
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-sm font-bold text-zinc-700 hover:text-zinc-900"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </button>
          <Button
            data-testid="glossary-print"
            onClick={() => window.print()}
            className="h-10 px-4 bg-zinc-900 text-white font-black"
          >
            <Printer className="w-4 h-4 mr-2" />
            Imprimir o guardar como PDF
          </Button>
        </div>
      </div>

      <article className="max-w-4xl mx-auto px-6 py-8 sm:py-10 print:py-4 print:px-4 text-zinc-900">
        {/* Encabezado */}
        <header className="mb-8 print:mb-5">
          <p className="text-[10px] uppercase tracking-widest font-black text-zinc-500 mb-1">
            miapoyo · Defiende Tus Derechos
          </p>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight mb-1">
            Glosario del Formulario I-589
          </h1>
          <p className="text-sm text-zinc-600 leading-snug mb-3">
            {data.form_title_es} · {data.form_title_en}
          </p>
          <p className="text-xs text-zinc-500">
            Versión {data.version} · Fuente: {data.source}
          </p>
        </header>

        {/* Disclaimer */}
        <aside className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4 mb-7 print:mb-5">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-amber-700" />
            <p className="text-xs font-black uppercase tracking-wider text-amber-900">
              Información importante antes de comenzar
            </p>
          </div>
          <p className="text-sm text-zinc-800 leading-snug">{data.disclaimer}</p>
        </aside>

        {/* Reglas globales */}
        <section className="mb-7 print:mb-5">
          <h2 className="text-lg font-black mb-2">Reglas globales que aplican a todo el formulario</h2>
          <ul className="text-sm space-y-2 leading-snug">
            <li>
              <span className="font-bold">Nunca dejar casillas en blanco.</span>{" "}
              {data.global_rules.never_blank_fields.rule_es} Escriba la palabra{" "}
              <span className="font-mono font-bold">{data.global_rules.never_blank_fields.what_to_write_en}</span>
              . Deletreo: {data.global_rules.never_blank_fields.phonetic_spelling_es}.
            </li>
            <li>
              <span className="font-bold">Mayúsculas.</span> {data.global_rules.case_rule}
            </li>
            <li>
              <span className="font-bold">Formato de fecha.</span> {data.global_rules.date_format}
            </li>
            <li>
              <span className="font-bold">Países.</span> {data.global_rules.country_format}
            </li>
          </ul>
        </section>

        {/* Partes del formulario */}
        {Object.entries(data.parts).map(([partKey, part]) => (
          <PartBlock key={partKey} partKey={partKey} part={part} />
        ))}

        {/* Workflow Parte B */}
        {data.writing_workflow_for_part_b && (
          <section className="bg-blue-50 border-2 border-blue-200 rounded-xl p-5 mb-7 print:mb-5">
            <h3 className="text-base font-black mb-3 text-blue-900">
              Cómo redactar el relato del miedo (Parte B)
            </h3>
            <ol className="text-sm space-y-2 leading-snug list-decimal pl-5">
              {partBSteps.map(([k, v]) => (
                <li key={k}>{v}</li>
              ))}
            </ol>
            {data.writing_workflow_for_part_b.rule_prohibition && (
              <p className="mt-3 text-xs font-bold text-red-800 bg-red-50 border border-red-200 rounded-lg p-3 leading-snug">
                {data.writing_workflow_for_part_b.rule_prohibition}
              </p>
            )}
          </section>
        )}

        {/* Preguntas frecuentes */}
        {data.common_user_questions && (
          <section className="mb-7 print:mb-5">
            <h3 className="text-base font-black mb-3">Preguntas frecuentes</h3>
            <dl className="space-y-2 text-sm">
              {Object.entries(data.common_user_questions).map(([q, a]) => (
                <div key={q} className="border-l-4 border-zinc-300 pl-3">
                  <dt className="font-bold">{q}</dt>
                  <dd className="text-zinc-700">{a}</dd>
                </div>
              ))}
            </dl>
          </section>
        )}

        {/* Pie de página */}
        <footer className="border-t border-zinc-300 pt-5 mt-8 print:mt-5 text-xs text-zinc-600 leading-snug">
          <p className="mb-1 font-bold">Recordatorio legal</p>
          <p>
            Este glosario es material educativo de auto-ayuda autorizado por los Estándares Nacionales de Detención Basados en el Rendimiento del ICE (PBNDS 2011, Sección 6.3 — Law Libraries and Legal Material) y la doctrina federal de auto-representación civil (Pro Se).
            miapoyo NO es un despacho de abogados y NO ofrece asesoría legal individualizada.
          </p>
          <p className="mt-2">
            Firme y feche el formulario solamente el día de su audiencia, ante el juez de inmigración. Nunca antes.
          </p>
        </footer>
      </article>
    </div>
  );
}

function PartBlock({ partKey, part }) {
  return (
    <section className="mb-7 print:mb-4 print:break-inside-avoid">
      <h2 className="text-xl font-black mb-2 text-zinc-900 border-b-4 border-zinc-900 pb-1">
        {part.title_es}
      </h2>
      <p className="text-xs text-zinc-500 mb-3">{part.title_en}</p>
      {part.intro_es && <p className="text-sm text-zinc-700 mb-4 leading-snug italic">{part.intro_es}</p>}
      {part.general_rule_es && (
        <p className="text-sm text-zinc-800 mb-4 bg-zinc-50 border border-zinc-200 rounded-lg p-3 leading-snug">
          <span className="font-bold">Regla general:</span> {part.general_rule_es}
        </p>
      )}

      {/* Categorías protegidas (solo en Parte B) */}
      {part.protected_categories && (
        <div className="mb-5">
          <p className="text-xs font-black uppercase tracking-wider text-zinc-600 mb-2">
            Cinco categorías protegidas por la ley de asilo
          </p>
          <ul className="space-y-3">
            {part.protected_categories.map((cat) => (
              <li key={cat.id} className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                <p className="text-sm font-black text-indigo-900">
                  {cat.name_es} · {cat.name_en}
                </p>
                <p className="text-xs text-zinc-700 mt-1 leading-snug">{cat.definition_es}</p>
                <p className="text-xs text-zinc-600 mt-2 italic leading-snug">
                  Ejemplo en inglés: <span className="font-mono">&ldquo;{cat.example_en}&rdquo;</span>
                </p>
                {cat.sub_categories && (
                  <details className="mt-2">
                    <summary className="text-xs font-bold text-indigo-800 cursor-pointer">Sub-categorías</summary>
                    <ul className="text-xs text-zinc-700 mt-1 pl-4 list-disc space-y-0.5">
                      {cat.sub_categories.map((s) => (
                        <li key={s}>{s}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Ítems del formulario */}
      <div className="space-y-3">
        {part.items.map((item) => (
          <FieldItem key={item.id} item={item} />
        ))}
      </div>
    </section>
  );
}

function FieldItem({ item }) {
  return (
    <div className="border border-zinc-200 rounded-lg p-4 print:break-inside-avoid">
      <div className="flex items-start gap-2 mb-1.5">
        <FileText className="w-4 h-4 text-zinc-400 flex-shrink-0 mt-0.5" />
        <p className="text-[10px] font-mono font-bold text-zinc-500 uppercase tracking-wider">
          {item.id}
        </p>
      </div>
      <p className="text-sm font-black text-zinc-900 mb-0.5">{item.field_label_en}</p>
      <p className="text-xs text-zinc-600 italic mb-2">{item.field_label_es}</p>
      <p className="text-sm text-zinc-800 leading-snug mb-2">{item.instruction_voice_es}</p>
      <div className="bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 mb-2">
        <p className="text-[10px] font-black uppercase tracking-wider text-emerald-800 mb-0.5">
          Qué escribir en el papel
        </p>
        <p className="text-sm font-mono font-bold text-emerald-900">{item.what_to_write_en}</p>
      </div>
      {item.florence_tip && (
        <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 leading-snug">
          <span className="font-bold">Tip Florence:</span> {item.florence_tip}
        </p>
      )}
    </div>
  );
}
